import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { defineMessages } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'

// Constants
import { FROM, FIND_A_HOME_DROPDOWN_TAB_NAMES } from 'src/constants/navigation'

// Actions
import { setCreateClientFrom } from 'src/actions/navigation'
import { toggleAuthModal } from 'src/actions/homeSearchAuthModal'

// Components
import {
  DropdownSelect,
  DropdownItem,
  DropdownItemDivider,
} from 'src/Components'

// Selectors
import {
  selectMarketExploreRoute,
  selectBuyingReportRoute,
  selectRouteToSearch,
  selectRouteToSavedSearches,
  selectRouteToFavoriteListings,
} from 'src/store/selectors/routes'
import {
  selectHoShouldSeeMarketsNav,
  selectHoShouldSeeBuyerReportNav,
} from 'src/store/selectors/navigation'
import { selectClientIsGuest } from 'src/store/selectors/client'
import {
  selectHasHomebotListingsRebuildFeature,
  selectHomebotListingsFeature,
} from 'src/store/selectors/customerProfile'

// Utils
import history from 'src/history'

const propTypes = {
  activeDropdownItem: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
}

const FIND_A_HOME_DROPDOWN_SELECT = {
  default: defineMessages({
    label: {
      id: 'Navbar.findAHome.dropdownSelect.label',
      defaultMessage: 'Find a Home',
    },
    searchLabel: {
      id: 'Navbar.findAHome.dropdownSelect.searchLabel',
      defaultMessage: 'Search Homes',
    },
    exploreLabel: {
      id: 'Navbar.findAHome.dropdownSelect.exploreLabel',
      defaultMessage: 'Explore Markets',
    },
    buyLabel: {
      id: 'Navbar.findAHome.dropdownSelect.buyLabel',
      defaultMessage: 'Buying Report',
    },
    favoriteLabel: {
      id: 'Navbar.findAHome.dropdownSelect.favoriteLabel',
      defaultMessage: 'Favorite Listings',
    },
    savedLabel: {
      id: 'Navbar.findAHome.dropdownSelect.savedLabel',
      defaultMessage: 'Saved Searches',
    },
  }),
}

// Find a Home Navlink & Dropdown when viewing listings rebuild
const FindAHome = ({ activeDropdownItem, size }) => {
  const dispatch = useDispatch()
  const intl = useFormatMessage()

  const isGuest = useSelector(selectClientIsGuest)
  const searchEnabled = useSelector(selectHomebotListingsFeature)
  const searchRebuildEnabled = useSelector(
    selectHasHomebotListingsRebuildFeature
  )

  // Paths to various drop downs
  const buyingReportTo = useSelector(selectBuyingReportRoute)
  const favoriteListingsTo = useSelector(selectRouteToFavoriteListings)
  const marketsTo = useSelector(selectMarketExploreRoute)
  const savedSearchesTo = useSelector(selectRouteToSavedSearches)
  const searchTo = useSelector(selectRouteToSearch)

  const routeOrRedirectToListings = route =>
    searchRebuildEnabled ? history.push(route) : window.location.assign(route)

  const handlePrimaryClick = () => {
    if (searchEnabled) {
      routeOrRedirectToListings(searchTo)
    } else {
      dispatch(setCreateClientFrom(FROM.MARKET_TAB))
      history.push(marketsTo)
    }
  }

  const handleDropDownItemClick = ({
    path,
    callback,
    isListingsRoute,
  } = {}) => {
    if (isGuest && !isListingsRoute) {
      dispatch(toggleAuthModal(true))

      return
    }

    if (callback) {
      dispatch(callback)
    }

    return isListingsRoute
      ? routeOrRedirectToListings(path)
      : history.push(path)
  }

  const showMarketsDropDownItem = useSelector(selectHoShouldSeeMarketsNav)
  const showBuyersReportDropDownItem = useSelector(
    selectHoShouldSeeBuyerReportNav
  )

  const {
    BUYING_REPORT,
    DIVIDER,
    EXPLORE_MARKETS,
    FAVORITE_LISTINGS,
    SAVED_SEARCHES,
    SEARCH_HOMES,
  } = FIND_A_HOME_DROPDOWN_TAB_NAMES

  // NOTE - ORDER DEPENDENT - DO NOT CHANGE
  const dropDownItemsMapping = {
    [SEARCH_HOMES]: {
      title: intl(FIND_A_HOME_DROPDOWN_SELECT.default.searchLabel),
      onClick: () => routeOrRedirectToListings(searchTo),
      show: searchEnabled,
    },
    [EXPLORE_MARKETS]: {
      title: intl(FIND_A_HOME_DROPDOWN_SELECT.default.exploreLabel),
      onClick: () =>
        handleDropDownItemClick({
          path: marketsTo,
          callback: setCreateClientFrom(FROM.MARKET_TAB),
        }),
      show: isGuest || showMarketsDropDownItem,
    },
    [BUYING_REPORT]: {
      title: intl(FIND_A_HOME_DROPDOWN_SELECT.default.buyLabel),
      onClick: () => handleDropDownItemClick({ path: buyingReportTo }),
      show: isGuest || showBuyersReportDropDownItem,
    },
    [DIVIDER]: {
      show: searchEnabled,
    },
    [FAVORITE_LISTINGS]: {
      title: intl(FIND_A_HOME_DROPDOWN_SELECT.default.favoriteLabel),
      onClick: () =>
        handleDropDownItemClick({
          path: favoriteListingsTo,
          isListingsRoute: true,
        }),
      show: searchEnabled,
    },
    [SAVED_SEARCHES]: {
      title: intl(FIND_A_HOME_DROPDOWN_SELECT.default.savedLabel),
      onClick: () =>
        handleDropDownItemClick({
          path: savedSearchesTo,
          isListingsRoute: true,
        }),
      show: searchEnabled,
    },
  }

  const handleOnClick = key => {
    const item = dropDownItemsMapping[key]

    return item.onClick()
  }

  const renderBuyerNavDropDownItems = () => {
    const dropDownKeys = Object.keys(dropDownItemsMapping)

    return dropDownKeys
      .filter(key => {
        const item = dropDownItemsMapping[key]

        return item.show
      })
      .map(key => {
        const item = dropDownItemsMapping[key]

        if (key === 'divider') {
          return <DropdownItemDivider key={key} />
        }

        return (
          <DropdownItem
            className='dropdownItem'
            id={key}
            key={key}
            label={item.title}
            onClick={() => handleOnClick(key)}
            value={key}
          />
        )
      })
  }

  return (
    <DropdownSelect
      className='dropdownSelect'
      dataqa='findAHome'
      label={intl(FIND_A_HOME_DROPDOWN_SELECT.default.label)}
      onClick={handlePrimaryClick}
      selectedDropdownItem={activeDropdownItem}
      size={size}
    >
      {renderBuyerNavDropDownItems()}
    </DropdownSelect>
  )
}

FindAHome.propTypes = propTypes

export default FindAHome
